import React, { useCallback, useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import Box from '@mui/joy/Box';
import Sheet from '@mui/joy/Sheet';
import Button from '@mui/joy/Button';
import IconButton from '@mui/joy/IconButton';
import DashboardTable from '../../components/DashboardTable';
import DashboardPageHeader from '../../components/DashboardPageHeader';
import PageStack from '../../components/PageStack';
import { LAYOUT } from '../../theme/layoutTokens';
import Stack from '@mui/joy/Stack';
import CircularProgress from '@mui/joy/CircularProgress';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import DeleteRoundedIcon from '@mui/icons-material/DeleteRounded';
import { categoriesApi } from '../../api/categories.api';
import { Category } from '../../types/category';

const CategoryList: React.FC = () => {
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);

  const loadCategories = useCallback(async () => {
    try {
      const data = await categoriesApi.getAll();
      setCategories(data);
    } catch (error) {
      console.error('Error al cargar categorías:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadCategories();
  }, [loadCategories]);

  const handleDelete = async (reference: string) => {
    if (window.confirm('¿Estás seguro de eliminar esta categoría?')) {
      try {
        await categoriesApi.delete(reference);
        setCategories((prev) => prev.filter((c) => c.reference !== reference));
      } catch (error) {
        console.error('Error al eliminar categoría:', error);
      }
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <PageStack spacing={LAYOUT.stackSurfaceGap}>
      <DashboardPageHeader
        title="Categorías"
        action={
          <Stack direction="row" spacing={1} flexWrap="wrap">
            <Button component={RouterLink} to="/categories/new" color="primary">
              Nueva categoría
            </Button>
            <Button component={RouterLink} to="/categories/bulk" color="success" variant="soft">
              Creación masiva
            </Button>
          </Stack>
        }
      />

      <Sheet variant="outlined" sx={{ overflow: 'auto', borderRadius: 'md', boxShadow: 'sm', p: 0 }}>
        <DashboardTable stickyHeader>
          <thead>
            <tr>
              <th>Nombre</th>
              <th>Descripción</th>
              <th>Fecha de creación</th>
              <th style={{ textAlign: 'right' }}>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {categories.map((category) => (
              <tr key={category.reference}>
                <td>{category.name}</td>
                <td>{category.description || '—'}</td>
                <td>{new Date(category.createdAt).toLocaleDateString()}</td>
                <td>
                  <Stack direction="row" spacing={1} justifyContent="flex-end" flexWrap="wrap">
                    <IconButton
                      component={RouterLink}
                      to={`/categories/${category.reference}/edit`}
                      size="sm"
                      variant="plain"
                      color="primary"
                      title="Editar categoría"
                    >
                      <EditRoundedIcon />
                    </IconButton>
                    <IconButton
                      size="sm"
                      variant="plain"
                      color="danger"
                      onClick={() => handleDelete(category.reference)}
                      title="Eliminar categoría"
                    >
                      <DeleteRoundedIcon />
                    </IconButton>
                  </Stack>
                </td>
              </tr>
            ))}
          </tbody>
        </DashboardTable>
      </Sheet>
    </PageStack>
  );
};

export default CategoryList;
